import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAdminAuth } from "../../../hooks/useAdminAuth";
import { adminService, AdminRole, AdminAuthResponse } from "../../../services/admin/admin.service";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { ArenaLogo } from "@/components/common/ArenaLogo";
import { getDefaultRouteForRole } from "@/contexts/AdminAuthContext/context";

const ROLE_CONFIG: Record<string, { title: string; subtitle: string; allowedRoles: AdminRole[] }> = {
  admin: {
    title: "Admin Login",
    subtitle: "Enter your credentials to access the admin dashboard",
    allowedRoles: ["ADMIN", "SUPER_ADMIN"],
  },
  sales: {
    title: "Sales Login",
    subtitle: "Enter your credentials to access the sales dashboard",
    allowedRoles: ["SALES"],
  },
};

export function AdminLogin() {
  const { role: roleParam } = useParams<{ role: string }>();
  const config = ROLE_CONFIG[roleParam || "admin"] || ROLE_CONFIG.admin;

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAdminAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsLoading(true);

    try {
      const response = await adminService.login(email, password);

      if (response.mustChangePassword) {
        setSuccessMessage(
          "A password reset link has been sent to your email. Please check your inbox to set your new password."
        );
        return;
      }

      if (!response.role || !config.allowedRoles.includes(response.role)) {
        setError("You do not have permission to access this portal");
        return;
      }

      login(response as AdminAuthResponse);
      navigate(getDefaultRouteForRole(response.role));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Invalid email or password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className=" md:m-auto mx-auto w-[98%] md:w-max pt-4 md:pt-0 md:h-max">
      <div className="  h-full mx-auto flex flex-col items-center justify-center   md:px-5 md:py-7 py-6 bg-card border border-border rounded-[24px] shadow-[1px_5px_10px_0px_#D1D1D11A,4px_18px_19px_0px_#D1D1D117,9px_41px_25px_0px_#D1D1D10D,15px_73px_30px_0px_#D1D1D103,24px_115px_33px_0px_#D1D1D100] dark:shadow-none  ">
        <ArenaLogo className="w-12 mb-8 border border-black mx-auto mb- rounded-[12px] bg-[#f6f6f6]" />
        <div className="w-full  flex items-center justify-center px-6">
          <div className="w-full">
            <div className="mb-8">
              <h1 className="font-bold text-[24px] text-foreground/60 leading-[24px] tracking-[0.16px] text-center align-middle">
                {config.title}
              </h1>
              <p className="font-normal text-[14px] mt-4 tracking-normal text-center align-middle text-muted-foreground">
                {config.subtitle}
              </p>
            </div>
            {successMessage && (
              <div className="mb-4 p-4 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-lg">
                <p className="text-sm text-green-800 dark:text-green-300 text-center">{successMessage}</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4 pb-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#d7785b] hover:bg-[#d7785b]/90"
                disabled={isLoading}
              >
                {isLoading ? "Logging in..." : "Login"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
